import type { AdminUser } from '@/api/core/users';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { IdText } from '@/components/id-text';
import { ListNote, TABLE_BODY_CLASS } from '@/components/list-note';
import { LoadFailure } from '@/components/load-failure';
import { RowLink } from '@/components/row-link';
import { SearchField } from '@/components/search-field';
import { formatDateTimeWithOffset } from '@/lib/format';
import { isUuid } from '@/lib/validation';
import {
  Button,
  DataTable,
  ListPage,
  PageHeader,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { lifecycleStatusLabels, serviceStatusLabels, userRoleLabels } from './user-presentation';
import { hasUserAccess, userListQuery, userRuntime, type UserRuntime } from './user-queries';
import { userDetailHref, userListState } from './user-url';

export function UserListPage({ runtime = userRuntime }: { runtime?: UserRuntime }) {
  const session = useOperatorSession(runtime.session);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [lookupError, setLookupError] = useState('');
  const state = userListState(params);
  const allowed = hasUserAccess(runtime.session, session.generation);
  const list = useQuery(
    userListQuery(
      runtime,
      session.generation,
      {
        ...(state.beforeCreatedAt ? { beforeCreatedAt: state.beforeCreatedAt } : {}),
        ...(state.beforeUserId ? { beforeUserId: state.beforeUserId } : {}),
      },
      state.valid && allowed,
    ),
  );
  const returnTo = location.pathname + location.search;
  const columns: DataTableColumn<AdminUser>[] = [
    {
      key: 'user_id',
      sortable: false,
      header: '사용자',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <RowLink
            to={userDetailHref(row.user_id, returnTo)}
            aria-label={`사용자 ${row.user_id} 상세`}
            title={row.user_id}
          >
            <span className="tabular-nums">{row.user_id.slice(0, 8)}</span>
          </RowLink>
          <IdText value={row.user_id} label={`사용자 ID ${row.user_id}`} length={0} />
        </div>
      ),
    },
    {
      key: 'service_status',
      sortable: false,
      header: '서비스 상태',
      cell: (row) => serviceStatusLabels[row.service_status],
    },
    {
      key: 'lifecycle_status',
      sortable: false,
      header: '계정 상태',
      cell: (row) => lifecycleStatusLabels[row.lifecycle_status],
    },
    {
      key: 'registration_completed',
      sortable: false,
      header: '가입',
      cell: (row) => (row.registration_completed ? '완료' : '미완료'),
    },
    {
      key: 'role_bits',
      sortable: false,
      header: '서비스 역할',
      cell: (row) => {
        const roles = userRoleLabels(row.role_bits);
        return roles.length > 0 ? (
          roles.join(', ')
        ) : (
          <span className="text-muted-foreground">역할 없음</span>
        );
      },
    },
    {
      key: 'created_at',
      sortable: false,
      header: '가입 시각',
      cell: (row) => (
        <span className="tabular-nums">{formatDateTimeWithOffset(row.created_at)}</span>
      ),
    },
  ];

  if (session.status !== 'authorized') return null;
  const loading = list.isPending && list.fetchStatus === 'fetching';
  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title="사용자 관리"
          description="계정 상태와 서비스 역할을 확인하고 권한·일시정지를 관리합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      {allowed && state.valid ? (
        <>
          <ListPage.Toolbar
            left={
              <div className="flex flex-col gap-1">
                <SearchField
                  value=""
                  aria-label="사용자 ID로 열기"
                  placeholder="사용자 ID 입력"
                  onSearch={(text) => {
                    const id = text.toLowerCase();
                    if (!text) {
                      setLookupError('');
                      return;
                    }
                    if (!isUuid(id)) {
                      setLookupError('사용자 ID 형식을 확인해 주세요.');
                      return;
                    }
                    setLookupError('');
                    void navigate(userDetailHref(id, location.pathname + location.search));
                  }}
                />
                {lookupError && (
                  <p role="alert" className="caption text-destructive">
                    {lookupError}
                  </p>
                )}
              </div>
            }
          />
          <ListNote>
            이름·연락처 검색은 제공하지 않습니다. 가입 시각 최신순으로 한 번에 최대 50명을 보여 주며
            전체 건수와 정렬 변경은 지원하지 않습니다.
            <span className="block sm:hidden">표는 좌우로 스크롤할 수 있습니다.</span>
          </ListNote>
          <ListPage.Body className={TABLE_BODY_CLASS}>
            {list.isError ? (
              <LoadFailure
                error={list.error}
                subject="사용자 목록"
                onRetry={() => void list.refetch()}
              />
            ) : (
              <div
                className="min-w-0 overflow-x-auto"
                role="region"
                aria-label="사용자 목록"
                tabIndex={0}
              >
                <DataTable
                  columns={columns}
                  data={loading ? [] : (list.data?.items ?? [])}
                  rowKey={(row) => row.user_id}
                  isLoading={loading}
                  empty={{
                    title: '조회된 사용자가 없습니다',
                    description: '조회 가능한 사용자가 없거나 이 위치 이후에 사용자가 없습니다.',
                  }}
                />
              </div>
            )}
          </ListPage.Body>
          <ListPage.Pagination>
            <div className="flex flex-wrap items-center gap-3">
              {(state.beforeCreatedAt ?? state.beforeUserId) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setParams(new URLSearchParams());
                  }}
                >
                  첫 사용자부터
                </Button>
              )}
              {list.status === 'success' && list.data.nextCursor !== null && (
                <Button
                  disabled={list.isFetching}
                  onClick={() => {
                    const cursor = list.data.nextCursor;
                    if (!cursor) return;
                    setParams(
                      new URLSearchParams({
                        before_created_at: cursor.beforeCreatedAt,
                        before_user_id: cursor.beforeUserId,
                      }),
                    );
                  }}
                >
                  다음 사용자 보기
                </Button>
              )}
            </div>
          </ListPage.Pagination>
        </>
      ) : allowed ? (
        <div className="flex flex-col items-start gap-3 p-6" role="alert">
          <p>목록 주소가 올바르지 않습니다.</p>
          <Button nativeButton={false} role="link" variant="outline" render={<Link to="/users" />}>
            목록 처음으로
          </Button>
        </div>
      ) : (
        <div className="p-6" role="alert">
          현재 사용자 조회 권한이 없습니다. 관리자에게 권한을 확인해 주세요.
        </div>
      )}
    </ListPage>
  );
}
